/**
 * CompareSelector — pick a sync to overlay as baseline ghost bars.
 * Returns: { sync_id, project_id } or null to clear.
 */
import { useEffect, useState } from "react";
import { listProjects, listSyncs } from "../lib/api";
import { X, GitCompare } from "lucide-react";
import { useI18n } from "../context/I18nContext";

export default function CompareSelector({ open, onClose, value, onChange }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [syncsByProject, setSyncsByProject] = useState({});
  const [proj, setProj] = useState(value?.project_id || "");
  const [sync, setSync] = useState(value?.sync_id || "");

  useEffect(() => {
    if (!open) return;
    (async () => { const d = await listProjects(); setProjects(d.projects || []); })();
  }, [open]);

  useEffect(() => {
    if (!proj) return;
    (async () => {
      // Use functional setter to avoid `syncsByProject` in the deps array;
      // we only want this effect to run when `proj` changes.
      let alreadyHas = false;
      setSyncsByProject((p) => {
        alreadyHas = !!p[proj];
        return p;
      });
      if (alreadyHas) return;
      const d = await listSyncs(proj);
      setSyncsByProject((p) => ({ ...p, [proj]: d.syncs || [] }));
    })();
  }, [proj]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4" data-testid="compare-modal">
      <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-lg">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold flex items-center gap-1.5"><GitCompare size={14} /> {t("compare_title")}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-zinc-500">{t("compare_help")}</p>
          <div>
            <label className="cs-label block mb-1.5">{t("compare_project_label")}</label>
            <select value={proj} onChange={(e) => { setProj(e.target.value); setSync(""); }} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm" data-testid="compare-proj">
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="cs-label block mb-1.5">{t("compare_version_label")}</label>
            <select value={sync} onChange={(e) => setSync(e.target.value)} disabled={!proj} className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm font-mono disabled:bg-zinc-100" data-testid="compare-sync">
              <option value="">—</option>
              {(syncsByProject[proj] || []).map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}{s.is_baseline ? " ★" : ""}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => { onChange(null); onClose(); }} className="px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-100 rounded" data-testid="compare-clear">{t("compare_clear")}</button>
            <button onClick={() => { onChange(sync ? { project_id: proj, sync_id: sync } : null); onClose(); }} disabled={!sync} className="px-4 py-1.5 text-sm font-semibold bg-cs-accent text-white rounded-md hover:bg-cs-accentHover disabled:opacity-60" data-testid="compare-apply">{t("compare_apply")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
